import { TuiSkeleton } from '@taiga-ui/kit';
import { TuiSheetDialog } from '@taiga-ui/addon-mobile';
// angular and rxjs imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  debounceTime,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';

// 3rd-party imports
import {
  TuiButton,
  TuiCheckbox,
  TuiDialogContext,
  TuiDialogService,
  TuiLoader,
  TuiScrollbar,
} from '@taiga-ui/core';
import { TuiTable } from '@taiga-ui/addon-table'; // local imports
import { DeviceDetectorService } from '@core/services/device-detector';
import { IsLoadingService } from '@core/services/is-loading.service';
import { RequestGroupActionsComponent } from './components/request-group-actions/request-group-actions.component';
import { AlternativeRequestsWidgetComponent } from './components/alternative-requests-widget/alternative-requests-widget.component';
import { RequestListColumnsFiltersComponent } from './components/request-list-columns-filters/request-list-columns-filters.component';
import { RequestListFiltersButtonsComponent } from './components/request-list-filters-buttons/request-list-filters-buttons.component';
import { RequestListItemDesktopComponent } from './components/request-list-item-desktop/request-list-item-desktop.component';
import { RequestListItemMobileComponent } from './components/request-list-item-mobile/request-list-item-mobile.component';
import { RequestModel } from '@core/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TablePaginationComponent } from '@common/components';
import { AccountService } from '@core/services/account/account.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { RequestService } from '@core/services/request/request.service';
import {
  PolymorpheusComponent,
  PolymorpheusContent,
} from '@taiga-ui/polymorpheus';
import {
  ActiveFilterChip,
  FilterQuery,
} from '@features/requests/request-list/types';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  HIDE_COMPLETED_FILTER,
  INITIAL_DESKTOP_COLUMNS,
  INITIAL_DESKTOP_COLUMNS_WIDTH,
  INITIAL_DESKTOP_ROWS_PER_PAGE,
  REQUEST_LIST_STATE_KEY,
  SHOW_IN_WORK_FILTER,
  SHOW_TODAY_FILTER,
  SHOW_TOMORROW_FILTER,
  X_TABLE_COLUMNS,
  X_TABLE_COLUMNS_ORDER,
  X_TABLE_COLUMNS_WIDTHS,
  X_TABLE_ROWS_PER_PAGE,
  X_TABLE_ROWS_SETTINGS,
} from '@core/constants';
import { TranslatePipe } from '@ngx-translate/core';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { FormsModule } from '@angular/forms';
import { EmptyBlockComponent } from '@core/components';
import {
  deepMerge,
  hexToRgba,
  normalizeOutkeepTableSettings,
} from '@core/helpers';
import {
  ColumnsWidths,
  MainRequestFilesPayload,
  OutkeepSettings,
  RequestTableColumn,
  RowsSettings,
  UserSettings,
} from '@core/types';
import { SelectedIds } from '@common/types';
import { catchError } from 'rxjs/operators';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { LogisticRequestType, OutkeepPermission } from '@outkeep/shared';
import { RequestCreateDialogComponent } from '@features/requests/request-create/request-create-dialog.component';
import {
  ContractorFilterDialogComponent,
  ContractorFilterDialogData,
  ContractorFilterDialogResult,
} from './components/contractor-filter-dialog/contractor-filter-dialog.component';
import {
  OwnerFilterDialogComponent,
  OwnerFilterDialogData,
  OwnerFilterDialogResult,
} from './components/owner-filter-dialog/owner-filter-dialog.component';
import { canShowAssignedToMeFilter } from './assigned-to-me-filter.helpers';

@Component({
  selector: 'x-request-list',
  templateUrl: './request-list.component.html',
  styleUrl: './request-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiSkeleton,
    AsyncPipe,
    AlternativeRequestsWidgetComponent,
    TuiLoader,
    RequestListItemMobileComponent,
    TuiTable,
    RequestListColumnsFiltersComponent,
    TuiCheckbox,
    TablePaginationComponent,
    RequestListFiltersButtonsComponent,
    RequestGroupActionsComponent,
    TuiSheetDialog,
    TuiScrollbar,
    TuiButton,
    NgClass,
    TranslatePipe,
    RequestListItemDesktopComponent,
    FormsModule,
    EmptyBlockComponent,
  ],
})
export class RequestListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestService = inject(RequestService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly accountService = inject(AccountService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly permissionService = inject(OutkeepPermissionService);
  readonly $listOfflineState = this.requestService.$listOfflineState;
  readonly $listSnapshotFetchedAt = this.requestService.$listSnapshotFetchedAt;
  readonly $isReadOnly = computed(() =>
    ['snapshot', 'unavailable'].includes(this.$listOfflineState()),
  );

  readonly $canCreateRequests = this.permissionService.$hasPermission(
    OutkeepPermission.LogistCreateRequests,
  );

  private readonly localStorageService = inject(LocalStorageService);
  private filterQuery: FilterQuery = {};
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public isInitialLoad = true;
  public loadedRequests: Signal<RequestModel[]> | undefined;
  public loadedRequestsCount: Signal<number> | undefined;

  public $selectedRequestsCountComputed: Signal<number> | undefined;

  public atLeastOneRequestSelected: Signal<boolean> | undefined;

  public requests$: Observable<MainRequestFilesPayload> | undefined = undefined;

  clearFilters$: Subject<void> = new Subject();

  public $filterIsActive = signal(false);
  public $canShowAssignedToMeFilter = signal(false);
  public $assignedToMeFilterIsActive = signal(false);
  // активность фильтров кнопок «Исполнитель» / «Заказчик» (подсветка кнопок)
  public $contractorFilterIsActive = signal(false);
  public $ownerFilterIsActive = signal(false);
  public $hideCompletedRequestsFilterIsActive = signal(false);
  public $inWorkRequestsFilterIsActive = signal(false);
  public $todayRequestsFilterIsActive = signal(false);
  public $tomorrowRequestsFilterIsActive = signal(false);
  public $isRepossessionMode = signal(false);

  public $selectAllRequests: Signal<undefined | boolean> | undefined;

  public $desktopTableColumns = signal<RequestTableColumn[]>(
    INITIAL_DESKTOP_COLUMNS,
  );
  public $desktopTableColumnsWidths = signal<ColumnsWidths>(
    INITIAL_DESKTOP_COLUMNS_WIDTH,
  );
  public $desktopTableRowsSettings = signal<RowsSettings>({});
  private saveTableSettings$ = new Subject<OutkeepSettings>();
  private tableSettings$: Observable<OutkeepSettings> =
    this.accountService.currentUserSettings$.pipe(
      map((userSettings: UserSettings | null) => userSettings?.outkeep),
      filter(Boolean),
    );

  public readonly deviceService = inject(DeviceDetectorService);

  public page = 1;
  public limit = INITIAL_DESKTOP_ROWS_PER_PAGE;

  public mobileGroupActionsOpened = false;
  public mobileFiltersSheetOpened = false;

  ngOnInit(): void {
    this.initComponentVariables();
    this.initDesktopTableColumns();
    this.initDesktopTableColumnsWidths();
    this.initDesktopTableRowsSettings();
    this.requestFiltersService.initFiltersData();
    this.initAssignedToMeFilterAvailability();

    this.requests$!.pipe(
      tap(() => {
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.saveTableSettings$
      .pipe(
        debounceTime(1000),
        tap((outkeepSettings: OutkeepSettings) => {
          this.accountService.saveToLocalStorage({ outkeep: outkeepSettings });
        }),
        switchMap((outkeepSettings: OutkeepSettings) => {
          return this.accountService.createOrUpdateUserSettings$({
            outkeep: outkeepSettings,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private initComponentVariables(): void {
    this.requestService.setLoadedRequests([]);
    this.requestService.setSelectedRequestsIds({});
    this.requests$ = this.route.queryParams.pipe(
      switchMap(params => {
        const paramsCopy = { ...params };

        // workaround param to force reload when params are the same
        if ('timestamp' in paramsCopy) {
          delete paramsCopy['timestamp'];
        }

        const rqQuery = this.parseRqQuery(params['rqQuery']);

        const limitParam = Number(params['limit']);

        if (limitParam > 0) {
          // если лимит есть в параметрах, то применим его
          this.limit = limitParam;
        } else {
          const savedLimit: number | null = this.localStorageService.getItem(
            X_TABLE_ROWS_PER_PAGE,
          );

          if (savedLimit) {
            // если лимит есть в локалсторадже, то применим его
            this.limit = savedLimit;
          } else {
            // дождемся настроек с сервера и позже применим если они есть
            this.getLimitFromSettings();
          }

          paramsCopy['limit'] = this.limit;
        }

        if (params['offset']) {
          this.page = +params['offset'] / this.limit + 1;
        } else {
          paramsCopy['offset'] = this.limit * (this.page - 1);
        }

        if (this.localStorageService.getItem(HIDE_COMPLETED_FILTER)) {
          this.hideCompletedRequests(true, false);
          rqQuery['statusId'] = { '!': 3 };
        }

        if (this.localStorageService.getItem(SHOW_IN_WORK_FILTER)) {
          this.showInWorkRequests(true, false);
          rqQuery['statusId'] = 10;
        }
        if (this.localStorageService.getItem(SHOW_TODAY_FILTER)) {
          this.showTodayRequests(true, false);
          rqQuery['releaseDatePeriod'] = {
            startDate: format(startOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'),
            endDate: format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'),
          };
        }
        if (this.localStorageService.getItem(SHOW_TOMORROW_FILTER)) {
          this.showTomorrowRequests(true, false);
          rqQuery['releaseDatePeriod'] = {
            startDate: format(
              startOfDay(addDays(new Date(), 1)),
              'yyyy-MM-dd HH:mm:ss',
            ),
            endDate: format(
              endOfDay(addDays(new Date(), 1)),
              'yyyy-MM-dd HH:mm:ss',
            ),
          };
        }

        if (Object.keys(rqQuery).length > 0) {
          paramsCopy['rqQuery'] = JSON.stringify({
            ...rqQuery,
          });
        }

        this.filterQuery = { ...(rqQuery as FilterQuery) };
        this.$assignedToMeFilterIsActive.set(rqQuery['assignedToMe'] === true);

        this.$isRepossessionMode.set(
          this.isOnlyRepossessionRequestType(rqQuery['requestType']),
        );

        // Подсветка кнопок «Исполнитель» / «Заказчик» — по итоговому rqQuery,
        // чтобы работала и при восстановлении фильтра из URL/localStorage
        const contractorFilterValue = rqQuery['contractorCounterpartyId'];

        this.$contractorFilterIsActive.set(
          (Array.isArray(contractorFilterValue)
            ? contractorFilterValue.length > 0
            : contractorFilterValue != null) ||
            rqQuery['contractorCounterpartySubdivisionId'] != null,
        );
        this.$ownerFilterIsActive.set(rqQuery['counterpartyOwnerId'] != null);

        return this.requestService.getAllRequestsByParam$(paramsCopy);
      }),
      map(response => {
        const { items, total } = response;
        const mainFiles =
          'mainFiles' in response ? response.mainFiles : undefined;

        if (mainFiles) {
          this.requestFilesService.setLoadedRequestsMainFiles(mainFiles);
        }

        return {
          requests: items.map(request => new RequestModel(request)),
          requestsCount: total,
        };
      }),
      map(({ requests, requestsCount }) => {
        this.requestService.setLoadedRequests(requests);
        this.requestService.setLoadedRequestsCount(requestsCount);

        // set selected requests ids keeping the already selected ones
        if (Object.keys(this.requestService.selectedRequestsIds()!)) {
          this.requestService.setSelectedRequestsIds({
            ...this.requestService.selectedRequestsIds(),
            ...this.requestService
              .loadedRequests()!
              .reduce<SelectedIds>((acc, request) => {
                if (
                  !(request.id in this.requestService.selectedRequestsIds()!)
                ) {
                  acc[request.id] = false;
                }

                return acc;
              }, {}),
          });
        } else {
          this.requestService.setSelectedRequestsIds(
            requests.reduce<SelectedIds>((acc, request) => {
              acc[request.id] = false;

              return acc;
            }, {}),
          );
        }
      }),
      switchMap(() => this.getMainFiles()),
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef),
    );

    this.loadedRequests = this.requestService.loadedRequests as Signal<
      RequestModel[]
    >;
    this.loadedRequestsCount = this.requestService
      .loadedRequestsCount as Signal<number>;
    this.$selectedRequestsCountComputed = computed(() => {
      return this.requestService.selectedRequestsCount();
    });
    this.atLeastOneRequestSelected = computed(() =>
      Object.values(this.requestService.selectedRequestsIds()!).some(
        isSelected => isSelected,
      ),
    );

    this.$selectAllRequests = computed(() => {
      if (this.atLeastOneRequestSelected!()) {
        const selectedRequests = Object.values(
          this.requestService.selectedRequestsIds()!,
        );

        if (selectedRequests.length === 0) {
          return false;
        }

        if (selectedRequests.every(value => value)) {
          return true;
        }
        if (selectedRequests.every(value => !value)) {
          return false;
        }
        if (selectedRequests.some(value => !value)) {
          return undefined;
        }
      }

      return false;
    });
  }

  private initDesktopTableColumns(): void {
    const savedColumns =
      this.localStorageService.getItem<RequestTableColumn[]>(X_TABLE_COLUMNS);

    if (savedColumns) {
      const normalizedStoredSettings = normalizeOutkeepTableSettings(
        {
          tableColumns: savedColumns,
          tableColumnsOrder: this.localStorageService.getItem(
            X_TABLE_COLUMNS_ORDER,
          ),
        },
        { applyDefaultColumns: true },
      );

      return this.$desktopTableColumns.set([
        ...(normalizedStoredSettings.tableColumns ?? []),
      ]);
    }

    this.tableSettings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tableSetting: OutkeepSettings) => {
        const normalizedTableSettings =
          normalizeOutkeepTableSettings(tableSetting);

        if (normalizedTableSettings.tableColumns) {
          this.$desktopTableColumns.set(normalizedTableSettings.tableColumns);
        }
      });
  }

  private initDesktopTableColumnsWidths(): void {
    const savedColumnsWidth = this.localStorageService.getItem<ColumnsWidths>(
      X_TABLE_COLUMNS_WIDTHS,
    );

    if (savedColumnsWidth) {
      const normalizedStoredSettings = normalizeOutkeepTableSettings(
        {
          tableColumnsWidths: savedColumnsWidth,
        },
        { applyDefaultColumns: false },
      );

      return this.$desktopTableColumnsWidths.set(
        normalizedStoredSettings.tableColumnsWidths!,
      );
    }

    this.tableSettings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tableSetting: OutkeepSettings) => {
        const normalizedTableSettings =
          normalizeOutkeepTableSettings(tableSetting);

        if (tableSetting.tableColumnsWidths) {
          this.$desktopTableColumnsWidths.set(
            normalizedTableSettings.tableColumnsWidths!,
          );
        }
      });
  }

  private initDesktopTableRowsSettings(): void {
    const savedRowsSettings = this.localStorageService.getItem<RowsSettings>(
      X_TABLE_ROWS_SETTINGS,
    );

    if (savedRowsSettings) {
      const normalizedStoredSettings = normalizeOutkeepTableSettings(
        {
          rowsSettings: savedRowsSettings,
        },
        { applyDefaultColumns: false },
      );

      return this.$desktopTableRowsSettings.set(
        normalizedStoredSettings.rowsSettings!,
      );
    }

    this.tableSettings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tableSetting: OutkeepSettings) => {
        const normalizedTableSettings =
          normalizeOutkeepTableSettings(tableSetting);

        if (normalizedTableSettings.rowsSettings) {
          this.$desktopTableRowsSettings.set(
            normalizedTableSettings.rowsSettings,
          );
        }
      });
  }

  private getLimitFromSettings(): void {
    this.tableSettings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tableSetting: OutkeepSettings) => {
        if (tableSetting.rowsPerPage) {
          this.onLimitChangeEvent(tableSetting.rowsPerPage);
        }
      });
  }

  ngOnDestroy(): void {
    this.requestService.unselectAllSelectedRequestsIds();
    this.requestService.setSelectedRequestsIds(null);
    this.requests$ = undefined;
    this.loadedRequests = undefined;
    this.loadedRequestsCount = undefined;
    this.$selectedRequestsCountComputed = undefined;
    this.$selectAllRequests = undefined;
    this.atLeastOneRequestSelected = undefined;
  }

  onTableSettingsChangedOutput(settings: OutkeepSettings): void {
    this.accountService.saveToLocalStorage({ outkeep: settings });
    const normalizedSettings = normalizeOutkeepTableSettings(
      deepMerge(
        {
          tableColumns: this.$desktopTableColumns(),
          tableColumnsWidths: this.$desktopTableColumnsWidths(),
          rowsSettings: this.$desktopTableRowsSettings(),
        },
        settings,
      ),
    );
    const { tableColumns, rowsSettings, tableColumnsWidths, rowsPerPage } =
      normalizedSettings;

    if (tableColumns) {
      this.$desktopTableColumns.set([...tableColumns]);
    }

    if (rowsSettings) {
      this.$desktopTableRowsSettings.set({ ...rowsSettings });
    }

    if (tableColumnsWidths === null) {
      this.$desktopTableColumnsWidths.set(INITIAL_DESKTOP_COLUMNS_WIDTH);
    }

    if (tableColumnsWidths) {
      this.$desktopTableColumnsWidths.set(tableColumnsWidths);
    }

    if (rowsPerPage) {
      this.onLimitChangeEvent(rowsPerPage);
    }

    this.saveTableSettings$.next(settings);
  }

  public onSelectAllRequestsChildEvent(allSelectedEvent?: boolean) {
    if (this.$isReadOnly()) return;
    if (allSelectedEvent === undefined) return;

    if (!allSelectedEvent) {
      this.requestService.unselectAllSelectedRequestsIds();
    } else {
      const updatedRequests = Object.fromEntries(
        this.requestService
          .loadedRequests()!
          .map(request => [request.id, true]),
      );

      this.requestService.setSelectedRequestsIds(updatedRequests);
    }
  }

  public onSelectRequestChildEvent(requestId: number) {
    if (this.$isReadOnly()) return;
    const selectedRequestsIds = this.requestService.selectedRequestsIds();

    this.requestService.setSelectedRequestsIds({
      ...selectedRequestsIds,
      [requestId]: !selectedRequestsIds![requestId],
    });
    this.$selectAllRequests!();
  }

  public onPageChangeEvent(page: number) {
    this.page = page;
    this.$filterIsActive.set(true);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { limit: this.limit, offset: this.limit * (this.page - 1) },
      queryParamsHandling: 'merge',
    });
  }

  public onLimitChangeEvent(limit: number) {
    this.limit = limit;
    this.$filterIsActive.set(true);
    this.saveTableSettings$.next({ rowsPerPage: limit });
    this.saveListState();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { limit: this.limit, offset: this.limit * (this.page - 1) },
      queryParamsHandling: 'merge',
    });
  }

  public onColumnFilterChanged(filterQuery: FilterQuery) {
    const filterIsActive = Object.keys(filterQuery).length > 0;

    if (!filterIsActive) {
      void this.clearFilters();
    }
    if (this.$hideCompletedRequestsFilterIsActive() && filterQuery.statusId) {
      this.hideCompletedRequests(false, false);
    }
    if (this.$inWorkRequestsFilterIsActive() && filterQuery.statusId) {
      this.showInWorkRequests(false, false);
    }
    if (this.$todayRequestsFilterIsActive() && filterQuery.releaseDatePeriod) {
      this.showTodayRequests(false, false);
    }
    if (
      this.$tomorrowRequestsFilterIsActive() &&
      filterQuery.releaseDatePeriod
    ) {
      this.showTomorrowRequests(false, false);
    }
    if (filterQuery.installerId?.length) {
      this.$assignedToMeFilterIsActive.set(false);
      delete filterQuery.assignedToMe;
    }
    this.filterQuery = filterQuery;
    this.$filterIsActive.set(filterIsActive);
  }

  public onFilterIsActiveOutput(isActive: boolean) {
    this.$filterIsActive.set(isActive);
  }

  public async clearFilters(): Promise<void> {
    if (this.$hideCompletedRequestsFilterIsActive()) {
      this.hideCompletedRequests(false, false);
    }
    if (this.$inWorkRequestsFilterIsActive()) {
      this.showInWorkRequests(false, false);
    }
    if (this.$todayRequestsFilterIsActive()) {
      this.showTodayRequests(false, false);
    }
    if (this.$tomorrowRequestsFilterIsActive()) {
      this.showTomorrowRequests(false, false);
    }
    if (this.$assignedToMeFilterIsActive()) {
      this.showAssignedToMeRequests(false, false);
    }
    this.page = 1;
    this.limit = 10;
    this.filterQuery = {};
    this.$filterIsActive.set(false);
    // Удаляем сохранённое состояние ДО навигации, иначе гард
    // RestoreRequestListStateGuard снова подставит только что сброшенный фильтр
    this.localStorageService.removeItem(this.getListStateKey());

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  public async searchRequests(): Promise<void> {
    const assignedToMe = this.$assignedToMeFilterIsActive()
      ? { assignedToMe: true }
      : {};

    const hideFinished = this.$hideCompletedRequestsFilterIsActive()
      ? { statusId: { '!': 3 } }
      : {};

    const showInWork = this.$inWorkRequestsFilterIsActive()
      ? { statusId: 10 }
      : {};

    const showToday = this.$todayRequestsFilterIsActive()
      ? {
          releaseDatePeriod: {
            startDate: format(startOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'),
            endDate: format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'),
          },
        }
      : {};

    const showTomorrow =
      this.$tomorrowRequestsFilterIsActive() &&
      !this.filterQuery.releaseDatePeriod
        ? {
            releaseDatePeriod: {
              startDate: format(
                startOfDay(addDays(new Date(), 1)),
                'yyyy-MM-dd HH:mm:ss',
              ),
              endDate: format(
                endOfDay(addDays(new Date(), 1)),
                'yyyy-MM-dd HH:mm:ss',
              ),
            },
          }
        : {};

    if (
      this.$hideCompletedRequestsFilterIsActive() ||
      this.$inWorkRequestsFilterIsActive()
    ) {
      delete this.filterQuery.statusId;
    }

    if (
      this.$todayRequestsFilterIsActive() ||
      this.$tomorrowRequestsFilterIsActive()
    ) {
      delete this.filterQuery.releaseDatePeriod;
    }

    if (this.$assignedToMeFilterIsActive()) {
      delete this.filterQuery.installerId;
    } else {
      delete this.filterQuery.assignedToMe;
    }

    const finalRqQuery = {
      ...this.filterQuery,
      // contractorCounterpartyId: DEFAULT_CONTRACTOR_COUNTERPARTY_ID,
      ...hideFinished,
      ...showInWork,
      ...showToday,
      ...showTomorrow,
      ...assignedToMe,
    };
    const hasFilters = Object.keys(finalRqQuery).length > 0;

    // Новый поиск всегда с первой страницы: иначе offset берётся от текущей страницы
    // (например, 10-й), и при меньшем числе найденных заявок страница пустая
    this.page = 1;

    // Сохраняем итоговый фильтр и лимит в localStorage до навигации,
    // чтобы гард RestoreRequestListStateGuard видел актуальное состояние
    this.saveListState(hasFilters ? finalRqQuery : undefined);

    const currentQueryParams = this.route.snapshot.queryParams;
    const isCurrentQueryParamsEmpty =
      Object.keys(currentQueryParams).length === 0;

    const queryParams = hasFilters
      ? {
          rqQuery: JSON.stringify(finalRqQuery),
        }
      : {
          ...(isCurrentQueryParamsEmpty
            ? { timestamp: new Date().getTime() }
            : {}),
        };

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  public toggleMobileGroupActions() {
    if (this.$isReadOnly()) return;
    this.mobileGroupActionsOpened = !this.mobileGroupActionsOpened;
  }

  public toggleMobileFiltersSheet() {
    this.mobileFiltersSheetOpened = !this.mobileFiltersSheetOpened;
  }

  public removeAdditionalFilter(filter: ActiveFilterChip) {
    const { title, value } = filter;

    if (Object.hasOwn(this.filterQuery, title)) {
      delete this.filterQuery[title];
    }
    if (title === 'statusId' && this.$inWorkRequestsFilterIsActive()) {
      this.showInWorkRequests(false);
    }
    if (title === 'installerGroup') {
      this.requestFiltersService.filtersData$
        .pipe(take(1))
        .subscribe(({ installersGroups }) => {
          if (!installersGroups) return;

          const installersToDelete =
            installersGroups
              .find(group => group.name === value)
              ?.installers?.map(installer => installer.id) || [];

          this.filterQuery.installerId = (
            this.filterQuery.installerId || []
          ).filter(id => !installersToDelete.includes(id));
        });
    }
    if (title === 'releaseDatePeriod') {
      if (this.$todayRequestsFilterIsActive()) {
        this.showTodayRequests(false);
      }
      if (this.$tomorrowRequestsFilterIsActive()) {
        this.showTomorrowRequests(false);
      }
    }

    const filterIsActive = Object.keys(this.filterQuery).length > 0;

    if (!filterIsActive) {
      void this.clearFilters();
    }
    this.$filterIsActive.set(filterIsActive);
    void this.searchRequests();
  }

  public clearContractorFilter(): void {
    delete this.filterQuery.contractorCounterpartyId;
    delete this.filterQuery.contractorCounterpartySubdivisionId;
    this.$contractorFilterIsActive.set(false);
    this.updateRequestListAfterFilterReset();
  }

  public clearOwnerFilter(): void {
    delete this.filterQuery.counterpartyOwnerId;
    this.$ownerFilterIsActive.set(false);
    this.updateRequestListAfterFilterReset();
  }

  private updateRequestListAfterFilterReset(): void {
    const filterIsActive = Object.keys(this.filterQuery).length > 0;

    if (!filterIsActive) {
      void this.clearFilters();
    }
    this.$filterIsActive.set(filterIsActive);
    void this.searchRequests();
  }

  public openDesktopGroupActions(
    content: PolymorpheusContent<TuiDialogContext>,
  ) {
    if (this.$isReadOnly()) return;
    this.dialogService$
      .open<RequestGroupActionsComponent>(content, {
        size: 'l',
        closable: false,
        dismissible: true,
        appearance: 'taiga x-group-actions-modal',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openDesktopAdditionalFiltersModal(
    content: PolymorpheusContent<TuiDialogContext>,
  ) {
    this.dialogService$
      .open<RequestListFiltersButtonsComponent>(content, {
        size: 'l',
        closable: false,
        dismissible: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openCreateRequestDialog(): void {
    if (this.$isReadOnly()) return;
    this.dialogService$
      .open<RequestCreateDialogComponent>(
        new PolymorpheusComponent(RequestCreateDialogComponent),
        {
          size: 'l',
          appearance: this.deviceService.$isMobile() ? 'fullscreen' : 'taiga',
          closable: true,
          dismissible: false,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public formatSnapshotTime(): string {
    const fetchedAt = this.$listSnapshotFetchedAt();

    return fetchedAt ? new Date(fetchedAt).toLocaleString('ru-RU') : '';
  }

  /** Открывает модалку фильтра «Исполнитель» (порт #subdivisionModal) */
  public openContractorFilterDialog(): void {
    this.dialogService$
      .open<ContractorFilterDialogResult>(
        new PolymorpheusComponent(ContractorFilterDialogComponent),
        {
          size: 'm',
          dismissible: true,
          data: {
            contractorCounterpartyId: this.filterQuery.contractorCounterpartyId,
            contractorCounterpartySubdivisionId:
              this.filterQuery.contractorCounterpartySubdivisionId,
          } satisfies ContractorFilterDialogData,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        // пустой результат = «Сбросить» — убираем оба поля из фильтра
        if (result.contractorCounterpartyId?.length) {
          this.filterQuery.contractorCounterpartyId =
            result.contractorCounterpartyId;
        } else {
          delete this.filterQuery.contractorCounterpartyId;
        }
        if (result.contractorCounterpartySubdivisionId) {
          this.filterQuery.contractorCounterpartySubdivisionId =
            result.contractorCounterpartySubdivisionId;
        } else {
          delete this.filterQuery.contractorCounterpartySubdivisionId;
        }
        void this.searchRequests();
      });
  }

  /** Открывает модалку фильтра «Заказчик» (порт #ownersModal) */
  public openOwnerFilterDialog(): void {
    this.dialogService$
      .open<OwnerFilterDialogResult>(
        new PolymorpheusComponent(OwnerFilterDialogComponent),
        {
          size: 'm',
          dismissible: true,
          data: {
            counterpartyOwnerId: this.filterQuery.counterpartyOwnerId,
          } satisfies OwnerFilterDialogData,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.counterpartyOwnerId) {
          this.filterQuery.counterpartyOwnerId = result.counterpartyOwnerId;
        } else {
          delete this.filterQuery.counterpartyOwnerId;
        }
        void this.searchRequests();
      });
  }

  public hideCompletedRequests(event: boolean, withSearch = true) {
    if (this.$inWorkRequestsFilterIsActive()) {
      this.$inWorkRequestsFilterIsActive.set(false);
      this.localStorageService.removeItem(SHOW_IN_WORK_FILTER);
    }

    this.$hideCompletedRequestsFilterIsActive.set(event);
    if (event) {
      if (!this.localStorageService.getItem(HIDE_COMPLETED_FILTER)) {
        this.localStorageService.setItem(HIDE_COMPLETED_FILTER, true);
      }
    } else {
      if (typeof this.filterQuery.statusId === 'object') {
        delete this.filterQuery.statusId;
      }
      if (this.localStorageService.getItem(HIDE_COMPLETED_FILTER)) {
        this.localStorageService.removeItem(HIDE_COMPLETED_FILTER);
      }
    }
    if (withSearch) {
      void this.searchRequests();
    }
  }

  public showAssignedToMeRequests(event: boolean, withSearch = true): void {
    this.$assignedToMeFilterIsActive.set(event);

    if (event) {
      delete this.filterQuery.installerId;
    } else {
      delete this.filterQuery.assignedToMe;
    }

    if (withSearch) {
      void this.searchRequests();
    }
  }

  public showInWorkRequests(event: boolean, withSearch = true) {
    if (this.$hideCompletedRequestsFilterIsActive()) {
      this.$hideCompletedRequestsFilterIsActive.set(false);
      this.localStorageService.removeItem(HIDE_COMPLETED_FILTER);
    }

    this.$inWorkRequestsFilterIsActive.set(event);
    if (event) {
      if (!this.localStorageService.getItem(SHOW_IN_WORK_FILTER)) {
        this.localStorageService.setItem(SHOW_IN_WORK_FILTER, true);
      }
    } else {
      if (typeof this.filterQuery.statusId === 'object') {
        delete this.filterQuery.statusId;
      }
      if (this.localStorageService.getItem(SHOW_IN_WORK_FILTER)) {
        this.localStorageService.removeItem(SHOW_IN_WORK_FILTER);
      }
    }
    if (withSearch) {
      void this.searchRequests();
    }
  }

  public showTodayRequests(event: boolean, withSearch = true) {
    if (this.$tomorrowRequestsFilterIsActive()) {
      this.$tomorrowRequestsFilterIsActive.set(false);
      this.localStorageService.removeItem(SHOW_TOMORROW_FILTER);
    }

    this.$todayRequestsFilterIsActive.set(event);
    if (event) {
      if (!this.localStorageService.getItem(SHOW_TODAY_FILTER)) {
        this.localStorageService.setItem(SHOW_TODAY_FILTER, true);
      }
      this.$tomorrowRequestsFilterIsActive.set(false);
    } else {
      if (typeof this.filterQuery.releaseDatePeriod === 'object') {
        delete this.filterQuery.releaseDatePeriod;
      }
      if (this.localStorageService.getItem(SHOW_TODAY_FILTER)) {
        this.localStorageService.removeItem(SHOW_TODAY_FILTER);
      }
    }
    if (withSearch) {
      void this.searchRequests();
    }
  }

  public showTomorrowRequests(event: boolean, withSearch = true) {
    if (this.$todayRequestsFilterIsActive()) {
      this.$todayRequestsFilterIsActive.set(false);
      this.localStorageService.removeItem(SHOW_TODAY_FILTER);
    }

    this.$tomorrowRequestsFilterIsActive.set(event);
    if (event) {
      if (!this.localStorageService.getItem(SHOW_TOMORROW_FILTER)) {
        this.localStorageService.setItem(SHOW_TOMORROW_FILTER, true);
      }
      this.$todayRequestsFilterIsActive.set(false);
    } else {
      if (typeof this.filterQuery.releaseDatePeriod === 'object') {
        delete this.filterQuery.releaseDatePeriod;
      }
      if (this.localStorageService.getItem(SHOW_TOMORROW_FILTER)) {
        this.localStorageService.removeItem(SHOW_TOMORROW_FILTER);
      }
    }
    if (withSearch) {
      void this.searchRequests();
    }
  }

  public onAuxClick(event: MouseEvent, request: RequestModel): void {
    if (event.ctrlKey || event.button === 1) {
      const link = `/requests/${request.requestNumber}/summary`;

      window.open(link, '_blank');
    }
  }

  private getMainFiles(): Observable<MainRequestFilesPayload> {
    const loadedRequestsIds = this.loadedRequests!().map(request => request.id);

    if (!loadedRequestsIds.length) {
      this.requestFilesService.setLoadedRequestsMainFiles({});

      return of({});
    }

    return this.requestFilesService
      .loadRequestsMainFile$(loadedRequestsIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(files => {
          this.requestFilesService.setLoadedRequestsMainFiles(files);
          void this.requestService.saveCurrentListMainFiles(files);
        }),
      );
  }

  protected readonly hexToRgba = hexToRgba;

  private initAssignedToMeFilterAvailability(): void {
    this.requestFiltersService.filtersData$
      .pipe(
        filter(
          ({ installers, installersLoadFailed }) =>
            installers !== null && !installersLoadFailed,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const canShowFilter = canShowAssignedToMeFilter(
          this.accountService.$currentUser(),
        );

        this.$canShowAssignedToMeFilter.set(canShowFilter);

        if (!canShowFilter && this.$assignedToMeFilterIsActive()) {
          this.showAssignedToMeRequests(false);
        }
      });
  }

  private isOnlyRepossessionRequestType(value: unknown): boolean {
    const requestTypes = Array.isArray(value) ? value : value ? [value] : [];

    return (
      requestTypes.length === 1 &&
      requestTypes[0] === LogisticRequestType.Repossession
    );
  }

  // Ключ localStorage с состоянием единого списка
  private getListStateKey(): string {
    return REQUEST_LIST_STATE_KEY;
  }

  /**
   * Сохраняет состояние списка (фильтр + лимит) в localStorage.
   * При следующем заходе на список без query-параметров гард
   * RestoreRequestListStateGuard подставит это состояние в URL.
   */
  private saveListState(rqQuery?: Record<string, unknown>): void {
    const state = {
      ...(rqQuery && Object.keys(rqQuery).length > 0 ? { rqQuery } : {}),
      limit: this.limit,
    };

    this.localStorageService.setItem(this.getListStateKey(), state);
  }

  private parseRqQuery(value: unknown): Record<string, unknown> {
    if (typeof value !== 'string') return {};

    try {
      const parsed: unknown = JSON.parse(value);

      return parsed !== null &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
        ? { ...parsed }
        : {};
    } catch {
      return {};
    }
  }
}
